import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPublicInvestments, submitContactRequest, PublicInvestment } from '../api/portalApi';
import { landingThemes } from '../utils/landingThemes';
import { getSessionToken } from '../utils/auth';
import AuthGate from '../components/common/AuthGate';
import { track } from '../utils/analytics';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import CurrencySwitcher from '../components/common/CurrencySwitcher';
import { useCurrency } from '../contexts/CurrencyContext';
import '../styles/join.css';

const DEFAULT_ACCENT = landingThemes[0]?.accent ?? '#38bdf8';

const CONFETTI_OFFSETS = [
  { x: '-120px', y: '-80px',  r: '45deg',   d: 0    },
  { x: '-80px',  y: '-130px', r: '-30deg',  d: 0.1  },
  { x: '-40px',  y: '-100px', r: '90deg',   d: 0    },
  { x: '0px',    y: '-140px', r: '-45deg',  d: 0.05 },
  { x: '40px',   y: '-110px', r: '60deg',   d: 0.1  },
  { x: '80px',   y: '-90px',  r: '-60deg',  d: 0.15 },
  { x: '120px',  y: '-120px', r: '120deg',  d: 0    },
  { x: '140px',  y: '-70px',  r: '-90deg',  d: 0.2  },
  { x: '-100px', y: '-50px',  r: '30deg',   d: 0.15 },
  { x: '-60px',  y: '-160px', r: '75deg',   d: 0.05 },
  { x: '60px',   y: '-160px', r: '-75deg',  d: 0.1  },
  { x: '100px',  y: '-60px',  r: '15deg',   d: 0.2  },
  { x: '-130px', y: '-110px', r: '-15deg',  d: 0.05 },
  { x: '130px',  y: '-140px', r: '30deg',   d: 0.15 },
  { x: '-20px',  y: '-180px', r: '-120deg', d: 0.1  },
  { x: '20px',   y: '-170px', r: '45deg',   d: 0.05 },
] as const;

const STATUS_COLOR: Record<string, string> = {
  active: '#22c55e',
  in_progress: '#38bdf8',
  waiting: '#fbbf24',
};

const JoinInvestment: React.FC = () => {
  const { investmentId } = useParams<{ investmentId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('join');

  const [investment, setInvestment] = useState<PublicInvestment | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [fullName, setFullName] = useState('');
  const [contactMethod, setContactMethod] = useState<'whatsapp' | 'email'>('whatsapp');
  const [contactDetail, setContactDetail] = useState('');
  const [rdvDate, setRdvDate] = useState('');
  const [note, setNote] = useState('');
  const [website, setWebsite] = useState(''); // honeypot — must stay empty

  const accent = DEFAULT_ACCENT;
  const { format: formatMoney } = useCurrency();

  useEffect(() => {
    track('join-view', { investmentId });
    getPublicInvestments()
      .then((r) => {
        const found = r.investments.find((inv) => inv._id === investmentId);
        setInvestment(found ?? null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [investmentId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!investmentId || !investment) return;

    setError('');
    setSaving(true);
    try {
      await submitContactRequest({
        investmentId,
        investmentTitle: investment.title,
        fullName: fullName.trim(),
        contactMethod,
        contactDetail: contactDetail.trim(),
        rdvDate,
        note: note.trim(),
        website,
      });
      track('join-submit', { investmentId, contactMethod });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.generic'));
    } finally {
      setSaving(false);
    }
  };

  const statusColor = investment ? (STATUS_COLOR[investment.status] ?? STATUS_COLOR['active']!) : null;

  return (
    <div className="join-shell" style={{ '--join-accent': accent } as React.CSSProperties}>
      {/* Nav */}
      <nav className="join-nav">
        <Link to="/" className="join-back-btn" onClick={() => track('nav-click', { label: 'back-home', from: 'join' })}>← {t('back')}</Link>
        <span className="join-nav-brand" style={{ color: accent }}>
          <img src="/logos/nomadme_sourcing_trading_mark.png" className="brand-logo" alt="" />
          NomadMe
        </span>
        <CurrencySwitcher variant="ghost" accentColor={accent} />
        <LanguageSwitcher variant="ghost" accentColor={accent} />
      </nav>

      {submitted ? (
        /* ── Success state ── */
        <div className="join-success" style={{ position: 'relative', overflow: 'hidden' }}>
          {/* Confetti burst */}
          <div className="confetti-container" aria-hidden="true">
            {CONFETTI_OFFSETS.map((o, i) => (
              <div
                key={i}
                className="confetti-piece"
                style={{
                  '--cx': o.x,
                  '--cy': o.y,
                  '--cr': o.r,
                  animationDelay: `${o.d}s`,
                  background: i % 3 === 0 ? accent : i % 3 === 1 ? '#a78bfa' : '#22d3ee',
                } as React.CSSProperties}
              />
            ))}
          </div>

          <div className="join-success-card">
            <span className="join-success-icon">🎉</span>
            <h2 className="join-success-title" style={{ fontSize: '2rem', marginBottom: 2 }}>{t('success.title')}</h2>
            <p className="join-success-welcome">{t('success.welcome')}</p>
            <p className="join-success-sub" style={{ marginTop: 16 }}>
              {contactMethod === 'whatsapp' ? t('success.subWhatsapp') : t('success.subEmail')}
            </p>
            <div className="join-success-rdv">
              {t('success.rdv', { date: new Date(rdvDate).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) })}
            </div>
            <div className="join-next-steps">
              <p className="join-next-title">{t('success.nextTitle')}</p>
              {[
                { icon: '📋', label: t('success.next1') },
                { icon: '📞', label: t('success.next2') },
                { icon: '🚀', label: t('success.next3') },
              ].map((s, i) => (
                <div key={i} className="join-next-step">
                  <span className="join-next-step-num">{i + 1}</span>
                  <span className="join-next-step-icon">{s.icon}</span>
                  <span className="join-next-step-label">{s.label}</span>
                </div>
              ))}
            </div>
            <Link
              to="/shop"
              className="join-shop-prompt"
              style={{ color: accent }}
              onClick={() => track('nav-click', { label: 'shop-from-join-success' })}
            >
              {t('success.shopPrompt')}
            </Link>
            <button
              type="button"
              onClick={() => { track('nav-click', { label: 'back-home', from: 'join-success' }); navigate('/'); }}
              style={{
                padding: '12px 28px',
                borderRadius: 12,
                border: 'none',
                background: `linear-gradient(90deg, ${accent}, #a78bfa)`,
                color: '#000',
                fontWeight: 800,
                fontSize: '0.88rem',
                cursor: 'pointer',
              }}
            >
              {t('backHome')}
            </button>
          </div>
        </div>
      ) : loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#475569' }}>
          {t('loading')}
        </div>
      ) : !investment ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, flexDirection: 'column', gap: 16 }}>
          <p style={{ color: '#475569' }}>{t('notFound')}</p>
          <Link to="/" className="join-back-btn" onClick={() => track('nav-click', { label: 'back-home', from: 'join-not-found' })}>← {t('backHome')}</Link>
        </div>
      ) : (
        <div className="join-body">
          {/* ── Left: investment info ── */}
          <div className="join-info-card">
            <p className="join-info-eyebrow" style={{ color: accent }}>{t('info.eyebrow')}</p>
            <h2 className="join-info-title">{investment.title}</h2>

            {statusColor && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 999, background: `${statusColor}18`, marginBottom: 16 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
                <span style={{ color: statusColor, fontWeight: 700, fontSize: '0.72rem' }}>{t(`status.${investment.status}`)}</span>
              </div>
            )}

            {investment.description && (
              <p className="join-info-desc">{investment.description}</p>
            )}

            <div className="join-info-rows">
              <div className="join-info-row">
                <span className="join-info-row-label">{t('info.minInvestment')}</span>
                <span className="join-info-row-value">{formatMoney(investment.minimumInvestment, investment.currency)}</span>
              </div>
              {investment.location && (
                <div className="join-info-row">
                  <span className="join-info-row-label">{t('info.region')}</span>
                  <span className="join-info-row-value">📍 {investment.location}</span>
                </div>
              )}
              <div className="join-info-row">
                <span className="join-info-row-label">{t('info.activeCargos')}</span>
                <span className="join-info-row-value">
                  {(investment as any).shippingType === 'air' ? '✈️' : (investment as any).shippingType === 'land' ? '🚛' : '🚢'}{' '}
                  {investment.cargoCount} cargo{investment.cargoCount !== 1 ? 's' : ''}
                </span>
              </div>
              {((investment as any).purchaseLocation || (investment as any).shippingDestination) && (
                <div className="join-info-row">
                  <span className="join-info-row-label">{t('info.route', 'Route')}</span>
                  <span className="join-info-row-value">
                    {(investment as any).purchaseLocation ?? '—'} → {(investment as any).shippingDestination ?? '—'}
                  </span>
                </div>
              )}
              <div className="join-info-row">
                <span className="join-info-row-label">{t('info.investorsInRound')}</span>
                <span className="join-info-row-value">👥 {investment.investorCount}</span>
              </div>
            </div>

            <p className="join-info-note">
              {t('info.note')}
            </p>
          </div>

          {/* ── Right: form ── */}
          <div className="join-form-wrap">
            <h1 className="join-form-title">{t('form.title')}</h1>
            <p className="join-form-sub">{t('form.sub')}</p>

            {!getSessionToken() ? (
              <AuthGate action="join this investment" accent={accent} context="Create a free account — no payment required at this stage." />
            ) : <form className="join-form" onSubmit={handleSubmit}>
              {/* Honeypot — hidden from humans; bots that fill it are dropped server-side. */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              />
              {/* Full name */}
              <div className="join-field">
                <label className="join-label">{t('form.fullName')}</label>
                <input
                  className="join-input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t('form.fullNamePlaceholder')}
                  required
                />
              </div>

              {/* Contact method */}
              <div className="join-field">
                <label className="join-label">{t('form.contactMethod')}</label>
                <div className="join-method-grid">
                  <button
                    type="button"
                    className={`join-method-card join-method-card--whatsapp${contactMethod === 'whatsapp' ? ' join-method-card--active' : ''}`}
                    onClick={() => { setContactMethod('whatsapp'); setContactDetail(''); track('join-contact-method', { method: 'whatsapp', investmentId }); }}
                  >
                    <span className="join-method-icon">📱</span>
                    <span className="join-method-name">{t('form.whatsapp')}</span>
                    <span className="join-method-desc">{t('form.whatsappDesc')}</span>
                  </button>
                  <button
                    type="button"
                    className={`join-method-card join-method-card--email${contactMethod === 'email' ? ' join-method-card--active' : ''}`}
                    onClick={() => { setContactMethod('email'); setContactDetail(''); track('join-contact-method', { method: 'email', investmentId }); }}
                  >
                    <span className="join-method-icon">✉️</span>
                    <span className="join-method-name">{t('form.email')}</span>
                    <span className="join-method-desc">{t('form.emailDesc')}</span>
                  </button>
                </div>
              </div>

              {/* Contact detail */}
              <div className="join-field">
                <label className="join-label">
                  {contactMethod === 'whatsapp' ? t('form.whatsappNumber') : t('form.emailAddress')}
                </label>
                <input
                  className="join-input"
                  type={contactMethod === 'email' ? 'email' : 'tel'}
                  value={contactDetail}
                  onChange={(e) => setContactDetail(e.target.value)}
                  placeholder={contactMethod === 'whatsapp' ? '+1 234 567 8900' : t('form.emailPlaceholder')}
                  required
                />
                {contactMethod === 'whatsapp' && (
                  <p className="join-field-hint">{t('form.countryCodeHint')}</p>
                )}
              </div>

              {/* RDV date */}
              <div className="join-field">
                <label className="join-label">{t('form.meetingDate')}</label>
                <input
                  className="join-input"
                  type="datetime-local"
                  value={rdvDate}
                  onChange={(e) => setRdvDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  required
                />
              </div>

              {/* Note */}
              <div className="join-field">
                <label className="join-label">{t('form.message')}</label>
                <textarea
                  className="join-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t('form.messagePlaceholder')}
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>

              {error && <p className="join-error">{error}</p>}

              <button
                type="submit"
                className="join-submit-btn"
                disabled={saving}
                style={{ background: `linear-gradient(90deg, ${accent}, #a78bfa)`, color: '#000' }}
              >
                {saving ? t('form.submitting') : t('form.submit')}
              </button>
            </form>}
          </div>
        </div>
      )}
    </div>
  );
};

export default JoinInvestment;
